/** Pipeline stages stored on `proposal_headers.pipelineStage`. */
export const PIPELINE_STAGES = ["draft", "prospect", "projected", "completed", "lost"] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export type ProposalPipelinePromotionTrigger =
  | "send_customer"
  | "generate_link_b"
  | "manual_promote"
  | "link_b_viewed";

export type ProposalPipelineDowngradeTrigger = "admin_downgrade";

export function normalisePipelineStage(value: string | null | undefined): PipelineStage | null {
  const v = String(value ?? "").trim().toLowerCase();
  if ((PIPELINE_STAGES as readonly string[]).includes(v)) return v as PipelineStage;
  return null;
}

export function isPipelineDraftStage(value: string | null | undefined): boolean {
  return normalisePipelineStage(value) === "draft";
}

export function isActivePipelineStage(value: string | null | undefined): boolean {
  const stage = normalisePipelineStage(value);
  return stage === "prospect" || stage === "projected";
}

export function countsInProspectPipelineTotals(value: string | null | undefined): boolean {
  return normalisePipelineStage(value) === "prospect";
}

export function countsInPipelineKpiTotals(value: string | null | undefined): boolean {
  const stage = normalisePipelineStage(value);
  return stage === "prospect" || stage === "projected" || stage === "completed" || stage === "lost";
}

/** Initial pipeline stage for newly created proposals. Cash-sale flows are customer-facing immediately. */
export function initialPipelineStageForNewProposal(options?: { cashSale?: boolean | null }): PipelineStage {
  return options?.cashSale ? "prospect" : "draft";
}

/** Legacy fallback when `pipelineStage` is null on older rows. */
export function inferPipelineStageFromLegacyFields(header: {
  pipelineStage?: string | null;
  status?: string | null;
}): PipelineStage {
  const explicit = normalisePipelineStage(header.pipelineStage);
  if (explicit) return explicit;
  const status = String(header.status ?? "").toLowerCase();
  if (status === "approved") return "projected";
  if (status === "rejected") return "lost";
  return "prospect";
}

export function pipelineStageDisplayLabel(stage: string | null | undefined): string {
  const normalised = normalisePipelineStage(stage);
  switch (normalised) {
    case "draft":
      return "Draft";
    case "prospect":
      return "Prospect";
    case "projected":
      return "Projected";
    case "completed":
      return "Completed";
    case "lost":
      return "Lost";
    default:
      return "Prospect";
  }
}
